import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { riderAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const RiderHistory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stats');
  const [filterShow, setFilterShow] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, historyRes] = await Promise.all([
          riderAPI.getStats(),
          riderAPI.getHistory()
        ]);
        setStats(statsRes.data);
        setHistory(historyRes.data.history || []);
      } catch (err) {
        toast.error('Failed to load rider data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredHistory = filterShow === 'all' 
    ? history 
    : history.filter(h => h.show_id === filterShow);

  const uniqueShows = [...new Map(history.map(h => [h.show_id, { id: h.show_id, name: h.show_name }])).values()];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-[#9E9E9E]">Loading your stats...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-8 px-4" data-testid="rider-history-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black text-white" style={{fontFamily: 'Outfit'}}>
              My Results & Stats
            </h1>
            <p className="text-[#9E9E9E] mt-1">{user?.name}'s performance history</p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard-btn"
            className="px-4 py-2 bg-[#161616] border border-[#262626] hover:border-[#0096FF] text-white rounded-md transition-colors text-sm"
          >
            Back to Dashboard
          </button>
        </div>

        {/* Tab Selector */}
        <div className="flex gap-1 bg-[#161616] p-1 rounded-lg mb-8 w-fit" data-testid="history-tabs">
          <button
            onClick={() => setActiveTab('stats')}
            data-testid="stats-tab"
            className={`px-5 py-2.5 rounded-md text-sm font-semibold transition-all ${activeTab === 'stats' ? 'bg-[#0096FF] text-white' : 'text-[#9E9E9E] hover:text-white'}`}
          >
            Statistics
          </button>
          <button
            onClick={() => setActiveTab('history')}
            data-testid="history-tab"
            className={`px-5 py-2.5 rounded-md text-sm font-semibold transition-all ${activeTab === 'history' ? 'bg-[#0096FF] text-white' : 'text-[#9E9E9E] hover:text-white'}`}
          >
            Run History
          </button>
        </div>

        {/* Stats Tab */}
        {activeTab === 'stats' && stats && (
          <div className="space-y-6">
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Total Shows" value={stats.total_shows} color="#0096FF" testId="stat-total-shows" />
              <StatCard label="Total Entries" value={stats.total_entries} color="#00D4FF" testId="stat-total-entries" />
              <StatCard label="Runs Scored" value={stats.total_runs_scored} color="#34C759" testId="stat-runs-scored" />
              <StatCard label="Earnings" value={`$${stats.total_earnings.toFixed(2)}`} color="#FFD700" testId="stat-earnings" />
            </div>

            {/* Performance Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-[#161616] border border-[#262626] rounded-lg p-5">
                <h3 className="text-sm font-medium text-[#9E9E9E] mb-3">Times</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[#C0C0C0] text-sm">Best Time</span>
                    <span className="text-white font-bold text-lg" data-testid="stat-best-time">
                      {stats.best_time ? `${stats.best_time}s` : '--'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[#C0C0C0] text-sm">Average Time</span>
                    <span className="text-white font-bold text-lg" data-testid="stat-avg-time">
                      {stats.average_time ? `${stats.average_time}s` : '--'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[#C0C0C0] text-sm">Slowest Time</span>
                    <span className="text-[#9E9E9E] font-bold text-lg">
                      {stats.worst_time ? `${stats.worst_time}s` : '--'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-[#161616] border border-[#262626] rounded-lg p-5">
                <h3 className="text-sm font-medium text-[#9E9E9E] mb-3">Placements</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[#C0C0C0] text-sm">1st Place Wins</span>
                    <span className="text-[#FFD700] font-bold text-lg" data-testid="stat-wins">{stats.win_count}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[#C0C0C0] text-sm">Top 3 Finishes</span>
                    <span className="text-[#C0C0C0] font-bold text-lg" data-testid="stat-top3">{stats.top3_count}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[#C0C0C0] text-sm">Top 5 Finishes</span>
                    <span className="text-white font-bold text-lg" data-testid="stat-top5">{stats.top5_count}</span>
                  </div>
                </div>
              </div>

              <div className="bg-[#161616] border border-[#262626] rounded-lg p-5">
                <h3 className="text-sm font-medium text-[#9E9E9E] mb-3">Reliability</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[#C0C0C0] text-sm">Completion Rate</span>
                    <span className="text-[#34C759] font-bold text-lg" data-testid="stat-completion-rate">
                      {stats.completion_rate}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[#C0C0C0] text-sm">No Times (NT)</span>
                    <span className="text-[#FF9500] font-bold text-lg">{stats.nt_count || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Placement Distribution */}
            {Object.keys(stats.placement_breakdown).length > 0 && (
              <div className="bg-[#161616] border border-[#262626] rounded-lg p-5">
                <h3 className="text-sm font-medium text-[#9E9E9E] mb-4">Placement Distribution</h3>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(stats.placement_breakdown)
                    .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
                    .map(([place, count]) => {
                      const colors = { '1': '#FFD700', '2': '#C0C0C0', '3': '#CD7F32' };
                      const color = colors[place] || '#0096FF';
                      return (
                        <div key={place} className="flex flex-col items-center px-4 py-3 bg-[#0A0A0A] rounded-lg min-w-[60px]">
                          <span className="text-xs text-[#9E9E9E]">
                            {place === '1' ? '1st' : place === '2' ? '2nd' : place === '3' ? '3rd' : `${place}th`}
                          </span>
                          <span className="text-xl font-bold mt-1" style={{color}}>{count}</span>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Shows List */}
            {stats.shows_list && stats.shows_list.length > 0 && (
              <div className="bg-[#161616] border border-[#262626] rounded-lg p-5">
                <h3 className="text-sm font-medium text-[#9E9E9E] mb-4">Shows Entered</h3>
                <div className="space-y-2">
                  {stats.shows_list.map((show, idx) => (
                    <div
                      key={idx}
                      onClick={() => navigate(`/shows/${show.show_id}`)}
                      className="flex justify-between items-center px-4 py-3 bg-[#0A0A0A] rounded-lg hover:bg-[#1a1a1a] cursor-pointer transition-colors"
                    >
                      <span className="text-white font-medium">{show.show_name}</span>
                      <span className="text-[#9E9E9E] text-sm">{show.show_date}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            {/* Filter */}
            <div className="flex items-center gap-3">
              <label className="text-sm text-[#9E9E9E]">Filter by show:</label>
              <select
                value={filterShow}
                onChange={(e) => setFilterShow(e.target.value)}
                data-testid="history-show-filter"
                className="px-3 py-2 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF]"
              >
                <option value="all">All Shows</option>
                {uniqueShows.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              <span className="text-[#9E9E9E] text-sm ml-auto">{filteredHistory.length} runs</span>
            </div>

            {/* History Table */}
            <div className="bg-[#161616] border border-[#262626] rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="history-table">
                  <thead>
                    <tr className="bg-[#0A0A0A]">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#9E9E9E] uppercase tracking-wide">Show</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#9E9E9E] uppercase tracking-wide">Class</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#9E9E9E] uppercase tracking-wide">Arena</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#9E9E9E] uppercase tracking-wide">Horse</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-[#9E9E9E] uppercase tracking-wide">Time</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-[#9E9E9E] uppercase tracking-wide">Knocks</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-[#9E9E9E] uppercase tracking-wide">Final</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-[#9E9E9E] uppercase tracking-wide">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHistory.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-12 text-[#9E9E9E]">No runs recorded yet</td>
                      </tr>
                    ) : (
                      filteredHistory.map((run, idx) => (
                        <tr key={idx} className="border-t border-[#262626] hover:bg-[#0A0A0A]/50 transition-colors">
                          <td className="px-4 py-3">
                            <div className="text-white text-sm font-medium">{run.show_name}</div>
                            <div className="text-[#9E9E9E] text-xs">{run.show_date}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-white text-sm">{run.class_name}</span>
                            {run.is_rollover && (
                              <span className="ml-1 text-[#FF9500] text-xs font-semibold">RO</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-[#C0C0C0] text-sm">{run.arena || '--'}</td>
                          <td className="px-4 py-3 text-[#C0C0C0] text-sm">{run.horse_name || '--'}</td>
                          <td className="px-4 py-3 text-right text-white text-sm font-mono">
                            {run.scored && run.time ? `${run.time}s` : '--'}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-mono">
                            {run.scored && run.knocks > 0 ? (
                              <span className="text-[#FF3B30]">+{run.knocks}</span>
                            ) : run.scored ? (
                              <span className="text-[#34C759]">0</span>
                            ) : '--'}
                          </td>
                          <td className="px-4 py-3 text-right text-white text-sm font-mono font-bold">
                            {run.final_time ? `${run.final_time}s` : '--'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {run.scored ? (
                              run.status === 'COMPLETED' ? (
                                <span className="px-2 py-1 bg-[#34C759]/20 text-[#34C759] text-xs font-semibold rounded">
                                  RAN
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-[#FF9500]/20 text-[#FF9500] text-xs font-semibold rounded">
                                  {run.status}
                                </span>
                              )
                            ) : (
                              <span className="px-2 py-1 bg-[#262626] text-[#9E9E9E] text-xs font-semibold rounded">
                                PENDING
                              </span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, color, testId }) => (
  <div className="bg-[#161616] border border-[#262626] rounded-lg p-5" data-testid={testId}>
    <p className="text-xs font-medium text-[#9E9E9E] uppercase tracking-wide mb-2">{label}</p>
    <p className="text-2xl font-black" style={{color, fontFamily: 'Outfit'}}>{value}</p>
  </div>
);

export default RiderHistory;
